import React, { useEffect, useMemo, useRef } from 'react';
import { DesignLayoutType, EventPageType, EventPageFragment } from '@graphql/generated';
import { Box, ColumnsV2, Flex, styled } from '@withjoy/joykit';
import { Footer } from '../../components/Footer';
import { Body } from './components/Body';
import { brannanConfig, useBrannanDesign } from './LayoutBrannan.theme';
import WelcomeVideo from '../../widgets/WelcomeVideo';
import CoverPhoto from '../../components/CoverPhoto';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useLayoutBrannanController } from './LayoutBrannan.controller';
import { findEventPageByPageSlug, getEventMenuPropsFromEvent } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { Shell } from './components/Shell';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import WebsiteHeader from './components/WebsiteHeader/WebsiteHeader';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings';
import { PagesRefs } from '../LayoutAloha/LayoutAloha.types';
import { useAlohaScroll } from '../LayoutAloha/hooks/useAlohaScroll';
import { CoverPhotoWrapper } from './components/CoverPhotoWrapper';
import { LayoutBrannanProps } from './LayoutBrannan';
import { PreloadImages } from '../LayoutAloha/components/PreloadImages/PreloadImages';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { StandaloneWebsiteHeader } from './components/StandaloneWebsiteHeader/StandaloneWebsiteHeader';
import { EventPageSlugs } from '@apps/admin/routes/Dashboard/common/enums/EventPageSlugs';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useColorPaletteProvider } from '../ColorPaletteProvider/ColorPaletteProvider';
import { StickyNavBar } from './components/StickyNavBar';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

const ScrollableContainer = styled(Box)`
  width: 100vw;
  height: 100vh;
  max-height: 100%;
  overflow: auto;
  z-index: 1;
`;

// Moments and RSVP are from mono repo which will be accessible via navbar but not scrolling
// Tidbits is already available as a part of page story
const PAGES_TO_BE_SKIPPED = [EventPageType.moments, EventPageType.rsvp, EventPageType.tidbits];

interface BodySectionProps {
  page: EventPageFragment;
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  isNativeAccommodations: boolean;
}

const BodySection = ({ page, event, eventHandle, websiteHeader, isNativeAccommodations }: BodySectionProps) => {
  return (
    <Body
      data-testid={`layout-body-${page.pageSlug}`}
      marginTop={[0, null, 9]}
      page={page}
      event={event}
      eventHandle={eventHandle}
      isSinglePage={true}
      photo={
        page.pageSlug === 'welcome' && event.video ? (
          <ColumnsV2.Column
            style={page.type === EventPageType.custom ? { display: isNativeAccommodations && !page?.subTitle ? 'none' : 'block' } : {}}
            span={[12, 10]}
            offset={[0, 1]}
          >
            <WelcomeVideo
              video={event.video}
              thumbnailUrl={page?.photo?.url || event.video.thumbnailUrl}
            />
          </ColumnsV2.Column>
        ) : page.pageSlug === 'welcome' && websiteHeader ? null : (
          <CoverPhotoWrapper
            renderFullColumn={page.photoPresentation?.presentationLayout !== 'center'}
            isCustomPage={page.type === EventPageType.custom}
            isNativeAccommodations={isNativeAccommodations}
            subTitle={page.subTitle}
          >
            <CoverPhoto
              websiteLayoutType={DesignLayoutType.brannan}
              photo={page.photo}
              pageId={page.id}
              pageType={page.type}
              pageSlug={page.pageSlug}
              photoPresentation={page.photoPresentation}
            />
          </CoverPhotoWrapper>
        )
      }
    />
  );
};

interface PageSectionProps
  extends Readonly<{
    page: EventPageFragment;
    event: LayoutBrannanProps['event'];
    eventHandle: string;
    websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
    pagesRef: Record<string, PagesRefs>;
  }> {}

const PageSectionAccommodationsPage = React.memo<PageSectionProps>(({ page, event, eventHandle, websiteHeader, pagesRef }) => {
  const { isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

  return (
    <Box
      paddingTop="6rem"
      key={page.id}
      ref={ref => (pagesRef[page.id] = { ref, page })}
    >
      <Flex
        data-testid={'page-custom'}
        justifyContent="center"
        alignItems="center"
        margin={'0 auto'}
      >
        <BodySection
          page={page}
          event={event}
          eventHandle={eventHandle}
          websiteHeader={websiteHeader}
          isNativeAccommodations={isNativeAccommodationPage}
        />
      </Flex>
    </Box>
  );
});

const PageSectionCustomPage = React.memo<PageSectionProps>(({ page, event, eventHandle, websiteHeader, pagesRef }) => {
  const { isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

  return (
    <Box
      paddingTop="6rem"
      key={page.id}
      ref={ref => (pagesRef[page.id] = { ref, page })}
    >
      <BodySection
        page={page}
        event={event}
        eventHandle={eventHandle}
        websiteHeader={websiteHeader}
        isNativeAccommodations={isNativeAccommodationPage}
      />
    </Box>
  );
});

const PageSection = React.memo<PageSectionProps>(props => {
  const { page, event, eventHandle, websiteHeader, pagesRef } = props;
  const { isAccommodationsPage, isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

  if (page.type === EventPageType.custom) {
    return isAccommodationsPage ? <PageSectionAccommodationsPage {...props} /> : <PageSectionCustomPage {...props} />;
  }

  return (
    <Box
      paddingTop="6rem"
      ref={ref => (pagesRef[page.id] = { ref, page })}
    >
      <BodySection
        page={page}
        event={event}
        eventHandle={eventHandle}
        websiteHeader={websiteHeader}
        isNativeAccommodations={isNativeAccommodationPage}
      />
    </Box>
  );
});

const PagesSection = React.memo<{
  filteredPages: EventPageFragment[];
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  pagesRef: Record<string, PagesRefs>;
}>(({ filteredPages, ...restProps }) => {
  return (
    <>
      {filteredPages.map((page, idx) => {
        return (
          <PageSection
            key={page.id}
            page={page}
            {...restProps}
          />
        );
      })}
    </>
  );
});

const LayoutBrannanSinglePage: React.FC<LayoutBrannanProps> = ({ eventHandle, event, setLoadingTheme }) => {
  const { info, pages, eventDesign } = event;
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobileOrTablet = useIsMobileOrTablet();
  const pagesRef = useMemo(() => ({}), []) as Record<string, PagesRefs>;

  const filteredPages = useMemo(() => pages.filter(page => page.type !== EventPageType.tidbits && !PAGES_TO_BE_SKIPPED.includes(page.type)), [pages]);
  useAlohaScroll(filteredPages, pagesRef, containerRef.current);
  const isPreviewing = isInIframe();

  const { handleMenuClicked, handleFooterButtonClicked, handleEditEventClicked } = useLayoutBrannanController(pages);
  const { currentPageSlug, isCustomPageSlug } = useCurrentPageSlug(eventHandle);
  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPageSlug);
  const [isStickyNavigationEnabled] = useIsFeatureEnabled('websiteStickyNavigationEnabled');

  const { websiteHeader } = useWebsiteHeaderSettings({
    eventHandle,
    targetWebsiteLayout: 'brannan',
  });

  const { isSmallScreen, loading } = useBrannanDesign(eventDesign);
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  const pagesSection = useMemo(
    () => (
      <PagesSection
        filteredPages={filteredPages}
        event={event}
        eventHandle={eventHandle}
        websiteHeader={websiteHeader}
        pagesRef={pagesRef}
      />
    ),
    [filteredPages, event, eventHandle, websiteHeader, pagesRef],
  );

  const headerProps = useMemo(() => {
    const currentPage = currentPageSlug ? findEventPageByPageSlug(pages, currentPageSlug, isCustomPageSlug) : null;

    return {
      page: currentPage?.type,
      pageSlug: currentPage?.pageSlug,
      pageId: currentPage?.id,
      isCustomPage: currentPage?.type === EventPageType.custom,
    };
  }, [currentPageSlug, isCustomPageSlug, pages]);

  useEffect(() => {
    setLoadingTheme && setLoadingTheme(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, headerProps.pageSlug]);

  return (
    <ScrollableContainer ref={containerRef}>
      {!isSmallScreen && isStickyNavigationEnabled && (
        <StickyNavBar
          scrollableContainerRef={containerRef}
          pages={pages}
          eventHandle={eventHandle}
          handleMenuClicked={handleMenuClicked}
          eventProps={getEventMenuPropsFromEvent(event)}
          pageColor={pageColor}
          textColor={pageFontColor}
          isFullWidth={websiteHeader?.headerPhoto?.presentationLayout === 'photoBackground'}
        />
      )}
      {websiteHeader && (
        <StandaloneWebsiteHeader
          websiteHeader={websiteHeader}
          navBarVariant={isSmallScreen ? 'menu' : 'inline'}
          pages={pages}
          currentPageType={EventPageType.welcome}
          eventHandle={eventHandle}
          handleMenuClicked={handleMenuClicked}
          eventProps={getEventMenuPropsFromEvent(event)}
          pageSlug={EventPageSlugs.Welcome}
          eventDesign={eventDesign}
        />
      )}
      <Shell
        eventDesign={event.eventDesign}
        isCustomPage={headerProps.isCustomPage}
      >
        <PreloadImages pages={filteredPages} />
        <Box paddingTop={isMobileOrTablet ? 0 : '6rem'}>
          {!isNativeAccommodationPage && (
            <WebsiteHeader
              data-testid={'layout-header'}
              websiteHeader={websiteHeader}
              applicatorProp={componentTitleBanner.prop}
              navBarVariant={isSmallScreen ? 'menu' : 'inline'}
              eventHandle={eventHandle}
              pages={pages}
              info={info}
              handleMenuClicked={handleMenuClicked}
              handleEditEventClicked={handleEditEventClicked}
              eventProps={getEventMenuPropsFromEvent(event)}
              page={headerProps.page}
              pageSlug={headerProps.pageSlug}
              pageId={headerProps.pageId}
              eventDisplayName={info?.eventDisplayName}
            />
          )}
        </Box>
        {pagesSection}
        <Flex __css={brannanFooterContainerStyles}>
          <Footer
            onButtonClicked={handleFooterButtonClicked}
            data-testid={'layout-footer'}
          />
        </Flex>
      </Shell>
    </ScrollableContainer>
  );
};

LayoutBrannanSinglePage.displayName = 'LayoutBrannanSinglePage';

export { LayoutBrannanSinglePage };
